/**
 * documentCategoryFromPath — canonical path → document-category
 * mapping.
 *
 * Consumers:
 *   - AP4 appendix grouped document list (`DocumentList.grouped`)
 *
 * Export surface (stable):
 *   - `documentCategoryFromPath(rawPath): DocumentCategory`
 *   - `DOCUMENT_CATEGORY_ORDER: DocumentCategory[]` (display order)
 *   - `DOCUMENT_CATEGORY_LABEL: Record<DocumentCategory, string>`
 *   - `cleanDocFileName` — re-exported from MV5's canonical
 *
 * Path inputs may include the full Drive prefix (e.g.
 * `Redhead Industrial Park/Site Control/POA Memo.pdf`); the
 * matching reads any substring of the path so callers can hand the
 * raw value through without prior stripping.
 *
 * Order of matching matters — the first rule that hits wins.
 * Electric is listed before Water so a "Substation Will-Serve
 * Letter" lands in Electric, not Water.
 */
export type DocumentCategory =
  | 'Site Control'
  | 'Title & Survey'
  | 'Permitting'
  | 'Water'
  | 'Electric'
  | 'Sales / Marketing'
  | 'Design'
  | 'Formation & Legal'
  | 'Financial'
  | 'Geotechnical'
  | 'Other';

/** Per-category path patterns — first match wins. Case-insensitive.
 *  Ordering matters: Electric is checked before Water so a
 *  "Substation Will-Serve Letter" lands in Electric, not Water. */
const CATEGORY_RULES: Array<{ category: DocumentCategory; patterns: RegExp[] }> = [
  // Site Control — purchase options, MOUs, POAs, lease agreements.
  {
    category: 'Site Control',
    patterns: [
      /\b(site\s*control|purchase\s*option|memo\s*of\s*p\s*o\s*a|p\.?o\.?a\.?|option\s*agreement|lease\s*agreement|easement|right[-\s]of[-\s]way|ground\s*lease)\b/i,
      /\bMOU\b/i,
    ],
  },
  // Title & Survey — title commitments, surveys, ALTA, plats, deeds, boundary.
  {
    category: 'Title & Survey',
    patterns: [
      /\b(title|prelim\s*title|alta|survey|plat|deed|boundary|legal\s*description)\b/i,
    ],
  },
  // Permitting & Zoning — zoning letters, conditional-use permits, env permits.
  {
    category: 'Permitting',
    patterns: [
      /\b(permit|permitting|zoning|conditional\s*use|cup|variance|special\s*exception|environmental\s*permit|nepa|sepa)\b/i,
      /\bP\s*&?\s*Z\b/i,
      /\bLPDES\b/i,
      /\bNPDES\b/i,
    ],
  },
  // Electric — interconnection, will-serve power, utility studies.
  // Ordered ABOVE Water so a "Substation Will-Serve Letter" lands here.
  {
    category: 'Electric',
    patterns: [
      /\b(electric|interconnect(ion)?|substation|will[-\s]serve\s*(letter|power)?|utility\s*study|isa\b|saa\b|generator\s*interconnect|utility)\b/i,
    ],
  },
  // Water — water rights, well logs, hydrology, aquifer, regional water boards.
  {
    category: 'Water',
    patterns: [
      /\b(water\s*rights?|well\s*log|hydrolog|aquifer|surface\s*water|groundwater|watershed)\b/i,
      /\bwater\b/i,
      /calcasieu/i,
      /\bWUC\b/i,
    ],
  },
  // Sales / Marketing — investor decks, marketing collateral, pitches.
  {
    category: 'Sales / Marketing',
    patterns: [/sales/i, /marketing/i, /\bdeck\b/i, /pitch/i],
  },
  // Design — site plans, layouts, architectural drawings, renderings.
  {
    category: 'Design',
    patterns: [/\bdesign\b/i, /site\s*plan/i, /\blayout\b/i, /\barchitect/i, /\brendering/i],
  },
  // Formation & Legal — articles of incorporation, operating agreements,
  // certificates of good standing, generic legal docs.
  {
    category: 'Formation & Legal',
    patterns: [
      /\b(formation|articles\s*of\s*(incorporation|organization)|operating\s*agreement|certificate\s*of\s*good\s*standing|bylaws|entity\s*filing)\b/i,
      /\bLLC\b/i,
      /\bcert\b/i,
      /\blegal\b/i,
    ],
  },
  // Financial — pro formas, financial models, CAPEX summaries, budgets.
  {
    category: 'Financial',
    patterns: [/financial/i, /pro\s*forma/i, /\bCAPEX\b/i, /\bmodel\b/i, /\bbudget\b/i],
  },
  // Geotechnical — soil reports, geology surveys, bearing-capacity studies.
  {
    category: 'Geotechnical',
    patterns: [/geotechnical/i, /\bsoil\b/i, /\bgeology\b/i, /bearing\s*capacity/i],
  },
];

/**
 * Map a document path (any depth) to one of the canonical buckets.
 * Returns `'Other'` when no rule fires — keep this as a real
 * category, not a sentinel, so the grouped UI always has a home for
 * the long tail of miscellaneous docs.
 */
export function documentCategoryFromPath(rawPath: string | null | undefined): DocumentCategory {
  if (!rawPath) return 'Other';
  for (const rule of CATEGORY_RULES) {
    for (const pattern of rule.patterns) {
      if (pattern.test(rawPath)) return rule.category;
    }
  }
  return 'Other';
}

/** Canonical display order. */
export const DOCUMENT_CATEGORY_ORDER: DocumentCategory[] = [
  'Site Control',
  'Title & Survey',
  'Permitting',
  'Water',
  'Electric',
  'Sales / Marketing',
  'Design',
  'Formation & Legal',
  'Financial',
  'Geotechnical',
  'Other',
];

/** Display label per category. Mostly identity; declared so
 *  renderers have a single import point. */
export const DOCUMENT_CATEGORY_LABEL: Record<DocumentCategory, string> = {
  'Site Control':       'Site Control',
  'Title & Survey':     'Title & Survey',
  'Permitting':         'Permitting & Zoning',
  'Water':              'Water',
  'Electric':           'Electric',
  'Sales / Marketing':  'Sales / Marketing',
  'Design':             'Design',
  'Formation & Legal':  'Formation & Legal Entity',
  'Financial':          'Financial',
  'Geotechnical':       'Geotechnical',
  'Other':              'Other',
};

// AP's local cleanDocFileName stub was superseded by the MV5
// canonical at `src/lib/docFileName.ts`. Re-export so existing AP
// callsites keep working without an import sweep.
export { cleanDocFileName } from './docFileName';
